import { Building } from '../constants';
import { CoreGameState } from '../GameEngine';
import { collisionLogger } from '../Logger';

import { CollisionService } from './CollisionService';
import {
  CollisionLayer,
  ResourceNodeEntity,
  BuildingEntity,
  TerrainEntity,
  CollisionResult,
  Entity,
} from './CollisionTypes';
import { getAllEntitiesFromGameState } from './EntityConverters';

/**
 * Collision system logging and analysis utilities
 */

/**
 * Log collision system initialization state
 */
export function logCollisionSystemState(
  gameState: CoreGameState,
  buildingLookup: (character: string) => Building | null
): void {
  // Only run expensive analysis if collision logging is enabled
  if (!collisionLogger.isEnabled()) {
    return;
  }

  // For logging purposes, we'll use a simple getTile that only checks gameMap (legacy behavior)
  const entities = getAllEntitiesFromGameState(
    gameState,
    buildingLookup,
    (x, y, gameMap) => gameMap[`${x},${y}`]
  );

  const entitiesByType: Record<string, number> = {};
  const entitiesByLayer: Record<string, number> = {};

  for (const entity of entities) {
    entitiesByType[entity.type] = (entitiesByType[entity.type] || 0) + 1;
    entitiesByLayer[entity.collisionLayer] =
      (entitiesByLayer[entity.collisionLayer] || 0) + 1;
  }

  // Count resource nodes specifically
  const resourceNodes = entities.filter(e => e.type === 'resource_node');
  const resourceNodesByType: Record<string, number> = {};

  resourceNodes.forEach(node => {
    const resourceType = (node as ResourceNodeEntity).resourceType || 'unknown';
    resourceNodesByType[resourceType] =
      (resourceNodesByType[resourceType] || 0) + 1;
  });

  // Count buildings by category
  const buildings = entities.filter(e => e.type === 'building');
  const buildingsByCategory: Record<string, number> = {};

  buildings.forEach(building => {
    const category =
      (building as BuildingEntity).building?.category || 'unknown';
    buildingsByCategory[category] = (buildingsByCategory[category] || 0) + 1;
  });

  collisionLogger.info('Collision system state analysis', {
    totalEntities: entities.length,
    entitiesByType,
    entitiesByLayer,
    resourceNodes: {
      total: resourceNodes.length,
      byType: resourceNodesByType,
    },
    buildings: {
      total: buildings.length,
      byCategory: buildingsByCategory,
    },
  });
}

/**
 * Log collision test at specific position
 */
export function logCollisionTest(
  collisionService: CollisionService,
  x: number,
  y: number,
  context: string = 'collision_test'
): void {
  const allEntities = collisionService.getEntitiesAt(x, y);
  const terrainEntities = collisionService.getEntitiesAt(x, y, {
    layers: [CollisionLayer.TERRAIN],
  });
  const buildingEntities = collisionService.getEntitiesAt(x, y, {
    layers: [CollisionLayer.BUILDINGS],
  });
  const resourceEntities = collisionService.getEntitiesAt(x, y, {
    layers: [CollisionLayer.RESOURCES],
  });

  collisionLogger.info(`Collision test at position (${context})`, {
    position: { x, y },
    totalEntities: allEntities.length,
    terrain: terrainEntities.map(e => ({
      id: e.id,
      terrainType: (e as TerrainEntity).terrainType,
      isSolid: e.isSolid,
    })),
    buildings: buildingEntities.map(e => ({
      id: e.id,
      buildingId: (e as BuildingEntity).building?.id,
      category: (e as BuildingEntity).building?.category,
      isSolid: e.isSolid,
    })),
    resources: resourceEntities.map(e => ({
      id: e.id,
      resourceType: (e as ResourceNodeEntity).resourceType,
    })),
    allEntitySummary: allEntities.map(e => ({
      id: e.id,
      type: e.type,
      layer: e.collisionLayer,
      isSolid: e.isSolid,
    })),
  });
}

/**
 * Log building placement validation attempt
 */
export function logBuildingPlacementTest(
  collisionService: CollisionService,
  building: Building,
  x: number,
  y: number
): void {
  const validationResult = collisionService.validateBuildingPlacement(
    building,
    x,
    y
  );
  const entitiesAtPosition = collisionService.getEntitiesAt(x, y);

  collisionLogger.info('Building placement validation test', {
    building: {
      id: building.id,
      name: building.name,
      category: building.category,
      size: building.size,
    },
    position: { x, y },
    validation: {
      isValid: validationResult.isValid,
      errors: validationResult.errors,
      warnings: validationResult.warnings,
      blockingEntitiesCount: validationResult.blockingEntities.length,
    },
    entitiesAtPosition: entitiesAtPosition.map(e => ({
      id: e.id,
      type: e.type,
      layer: e.collisionLayer,
      isSolid: e.isSolid,
      bounds: e.bounds,
    })),
    blockingEntities: validationResult.blockingEntities.map(e => ({
      id: e.id,
      type: e.type,
      layer: e.collisionLayer,
      reason: 'blocking placement',
    })),
  });
}

/**
 * Log player movement collision check
 */
export function logPlayerMovementCollision(
  collisionService: CollisionService,
  fromPos: { x: number; y: number },
  toPos: { x: number; y: number },
  collisionResult: CollisionResult
): void {
  if (!collisionResult.canMove) {
    // Only log blocked movements to avoid spam
    collisionLogger.info('Player movement blocked by collision', {
      movement: {
        from: fromPos,
        to: toPos,
        blocked: true,
      },
      collision: {
        reason: collisionResult.reason,
        blockingEntities: collisionResult.blockingEntities.map((e: Entity) => ({
          id: e.id,
          type: e.type,
          layer: e.collisionLayer,
          isSolid: e.isSolid,
        })),
      },
    });

    // Also test what entities are at the target position
    logCollisionTest(collisionService, toPos.x, toPos.y, 'blocked_movement');
  }
}

/**
 * Log resource node and mining collision issues
 */
export function logResourceNodeCollisionIssues(
  gameState: CoreGameState,
  collisionService: CollisionService,
  _buildingLookup: (character: string) => Building | null
): void {
  // Only run expensive analysis if collision logging is enabled
  if (!collisionLogger.isEnabled()) {
    return;
  }

  collisionLogger.info('Analyzing resource node collision issues');

  // Debug: Check what characters are actually in the game map
  const characterCount: Record<string, number> = {};
  let totalTiles = 0;

  for (const [, tileChar] of Object.entries(gameState.gameMap)) {
    totalTiles++;
    if (tileChar) {
      characterCount[tileChar] = (characterCount[tileChar] || 0) + 1;
    }
  }

  collisionLogger.info('Game map character analysis', {
    totalTiles,
    uniqueCharacters: Object.keys(characterCount).length,
    characterFrequency: Object.entries(characterCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15), // Top 15 most frequent characters
    expectedResourceChars: {
      iron: 'I',
      copper: 'C',
    },
  });

  // Find all resource node positions in game map
  const resourcePositions: Array<{ x: number; y: number; char: string }> = [];

  for (const [posKey, tileChar] of Object.entries(gameState.gameMap)) {
    if (tileChar === 'I' || tileChar === 'C') {
      // Iron or Copper
      const [xStr, yStr] = posKey.split(',');
      if (xStr && yStr) {
        const x = parseInt(xStr, 10);
        const y = parseInt(yStr, 10);
        if (!isNaN(x) && !isNaN(y)) {
          resourcePositions.push({ x, y, char: tileChar });
        }
      }
    }
  }

  collisionLogger.info('Resource nodes found in game map', {
    totalResourceNodes: resourcePositions.length,
    samples: resourcePositions.slice(0, 5),
  });

  // Check collision entities at resource positions
  let resourceEntitiesFound = 0;
  let terrainEntitiesAtResources = 0;

  for (const resourcePos of resourcePositions.slice(0, 10)) {
    // Check first 10
    const resourceEntities = collisionService.getEntitiesAt(
      resourcePos.x,
      resourcePos.y,
      {
        layers: [CollisionLayer.RESOURCES],
      }
    );
    const terrainEntities = collisionService.getEntitiesAt(
      resourcePos.x,
      resourcePos.y,
      {
        layers: [CollisionLayer.TERRAIN],
      }
    );

    if (resourceEntities.length > 0) {
      resourceEntitiesFound++;
    }
    if (terrainEntities.length > 0) {
      terrainEntitiesAtResources++;
    }

    collisionLogger.debug('Resource position analysis', {
      position: resourcePos,
      gameMapChar: resourcePos.char,
      resourceEntities: resourceEntities.length,
      terrainEntities: terrainEntities.length,
      resourceEntityDetails: resourceEntities.map(e => ({
        id: e.id,
        resourceType: (e as ResourceNodeEntity).resourceType,
      })),
      terrainEntityDetails: terrainEntities.map(e => ({
        id: e.id,
        terrainType: (e as TerrainEntity).terrainType,
      })),
    });
  }

  collisionLogger.info('Resource node collision analysis summary', {
    resourcePositionsChecked: Math.min(10, resourcePositions.length),
    resourceEntitiesFound,
    terrainEntitiesAtResources,
    issuesDetected: {
      missingResourceEntities:
        Math.min(10, resourcePositions.length) - resourceEntitiesFound,
      unexpectedTerrainAtResources: terrainEntitiesAtResources,
    },
  });
}

/**
 * Log miner placement collision issues
 */
export function logMinerPlacementCollisionIssues(
  collisionService: CollisionService,
  gameState: CoreGameState,
  buildingLookup: (character: string) => Building | null
): void {
  // Only run expensive analysis if collision logging is enabled
  if (!collisionLogger.isEnabled()) {
    return;
  }

  // Find miner building
  const minerBuilding = buildingLookup('M'); // Assuming M is miner character

  if (!minerBuilding) {
    collisionLogger.warn('Could not find miner building for collision testing');
    return;
  }

  collisionLogger.info('Testing miner placement collision', {
    minerBuilding: {
      id: minerBuilding.id,
      name: minerBuilding.name,
      category: minerBuilding.category,
    },
  });

  // Test miner placement on known resource positions
  for (const [posKey, tileChar] of Object.entries(gameState.gameMap)) {
    if (tileChar === 'I' || tileChar === 'C' || tileChar === '*') {
      const [xStr, yStr] = posKey.split(',');
      if (xStr && yStr) {
        const x = parseInt(xStr, 10);
        const y = parseInt(yStr, 10);
        if (!isNaN(x) && !isNaN(y)) {
          // Test first resource node found
          logBuildingPlacementTest(collisionService, minerBuilding, x, y);
          break; // Only test one for now
        }
      }
    }
  }
}
